import api from './api';

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export const inferMediaKind = (media) => {
  const value = media || {};
  if (value.kind) return value.kind;
  const type = value.mimetype || value.type || '';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'document';
};

export const uploadToResumableUrl = ({ url, file, onProgress }) => new Promise((resolve, reject) => {
  const request = new XMLHttpRequest();
  request.open('PUT', url, true);
  request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
  request.upload.onprogress = (event) => {
    if (event.lengthComputable && onProgress) {
      onProgress(Math.round((event.loaded / event.total) * 100));
    }
  };
  request.onerror = () => reject(new Error('The upload could not reach cloud storage.'));
  request.onabort = () => reject(new Error('The upload was cancelled.'));
  request.onload = () => {
    if (request.status >= 200 && request.status < 300) resolve();
    else reject(new Error(`Cloud storage rejected the upload (${request.status}).`));
  };
  request.send(file);
});

export const waitForMediaReady = async (assetId, {
  timeoutMs = 10 * 60 * 1000,
  intervalMs = 2000,
  onStatus,
} = {}) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const { data } = await api.get(`/case-media/${assetId}`);
    const media = data.media;
    onStatus?.(media);
    if (media.status === 'ready') return media;
    if (media.status === 'failed' || media.status === 'deleted') {
      throw new Error(media.errorMessage || 'The media file could not be processed.');
    }
    await wait(intervalMs);
  }
  throw new Error('Media processing is taking longer than expected. Please try again shortly.');
};

export const uploadCaseMedia = async ({ file, purpose, caseId, uploadSessionId, onProgress, onStatus }) => {
  const { data } = await api.post('/case-media/uploads', {
    originalName: file.name,
    contentType: file.type,
    size: file.size,
    purpose,
    caseId: caseId || null,
    uploadSessionId: uploadSessionId || '',
  });
  const media = data.media;
  try {
    await uploadToResumableUrl({ url: data.upload.url, file, onProgress });
    const completion = await api.post(`/case-media/${media.assetId}/complete`);
    onStatus?.(completion.data.media);
    return await waitForMediaReady(media.assetId, { onStatus });
  } catch (error) {
    try {
      await api.delete(`/case-media/${media.assetId}`);
    } catch {
      // Best-effort cleanup; the server also expires abandoned upload records.
    }
    throw error;
  }
};

export const getCaseMediaPlayback = async (media, rendition = 'auto') => {
  const assetId = media?.assetId || media?.id;
  if (!assetId) return null;
  const { data } = await api.get(`/case-media/${assetId}/playback`, { params: { rendition } });
  return data;
};

export const deleteCaseMedia = async (media) => {
  const assetId = media?.assetId || media?.id;
  if (!assetId) return;
  await api.delete(`/case-media/${assetId}`);
};
